import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as ChargingPoint } from '../../../assets/icons/dashboard/charging-point.svg';
import { ReactComponent as NavigateArrow } from '../../../assets/icons/dashboard/navigate-arrow.svg';
import { useAppDispatch } from '../../../redux/hooks';
import { updateDeviceFilters } from '../../../redux/slice/DeviceManagementSlice';
import { setNotificationDetails } from '../../../redux/slice/NotificationSlice';
import dashboard from '../../../utils/api/dashboard';

interface DeviceStatusProperties {
  status: 'Online' | 'Offline' | string;
  count: number;
}

/**
 * Device status component
 * @returns
 */
export default function DeviceStatus() {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [deviceStatusCount, setDeviceStatusCount] = useState<{ online: number; offline: number }>({
    online: 0,
    offline: 0,
  });

  /**
   * Function to fetch connector status report
   */
  const fetchConnectorStatusReport = () => {
    dashboard
      .getDeviceStatusReport()
      .then((response: { data: DeviceStatusProperties[] }) => {
        const result = response.data;

        const statusMap: { online: number; offline: number } = {
          online: 0,
          offline: 0,
        };

        result.forEach((item) => {
          const key = item.status.toLowerCase() as 'online' | 'offline';
          if (key === 'online' || key === 'offline') {
            statusMap[key] = item.count;
          }
        });

        setDeviceStatusCount(statusMap);
      })
      .catch((error: any) => {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };

  /**
   * Function to handle redirect device management page with filter
   */
  const handleClickPieChartCell = (key: string) => {
    dispatch(
      updateDeviceFilters({
        key: 'deviceStatus',
        value: key,
        type: 'restAndAdd',
      }),
    );
    navigate('/ev-admin/device-management');
  };

  useEffect(() => {
    fetchConnectorStatusReport();
  }, []);

  return (
    <div className="dashboard-device-status-wrap">
      <div className="dashboard-device-status-header">
        <div className="header-title">
          <h6>{t('deviceStatus')}</h6>
          <span
            className="header-arrow"
            role="presentation"
            onClick={() => navigate('/ev-admin/device-management')}>
            <NavigateArrow />
          </span>
        </div>
      </div>
      <div className="dashboard-device-status-container">
        <div className="device-status-top">
          <div
            className="outer-circle go-green"
            role="presentation"
            onClick={() => handleClickPieChartCell('online')}>
            <div className="inner-circle">{deviceStatusCount?.online}</div>
          </div>
          <div
            className="outer-circle need-attention"
            role="presentation"
            onClick={() => handleClickPieChartCell('offline')}>
            <div className="inner-circle">{deviceStatusCount?.offline}</div>
          </div>
          {/* <div className="outer-circle out-of-service">
            <div className="inner-circle">27</div>
          </div> */}
          <div className="absolute-container">
            <svg
              width="213"
              height="28"
              viewBox="0 0 213 28"
              fill="none"
              xmlns="http://www.w3.org/2000/svg">
              <path
                d="M1.22266 15.9421C9.76059 10.3572 33.8465 -0.315986 61.8869 1.66973C96.9373 4.15187 198.044 37.0397 212.199 23.3879"
                stroke="url(#paint0_linear_49_10663)"
                strokeWidth={1.26333}
              />
              <defs>
                <linearGradient
                  id="paint0_linear_49_10663"
                  x1="1.22266"
                  y1="14.0601"
                  x2="212.199"
                  y2="14.0601"
                  gradientUnits="userSpaceOnUse">
                  <stop stopColor="#00A79D" />
                  <stop offset="1" stopColor="#6EC282" />
                </linearGradient>
              </defs>
            </svg>
          </div>
        </div>
        <div className="device-status-bottom">
          <div className="outer-circle go-green">
            <div className="inner-circle">
              <ChargingPoint />
              <p>{t('greenAndGo')}</p>
            </div>
          </div>
          <div className="outer-circle need-attention">
            <div className="inner-circle">
              <ChargingPoint />
              <p>{t('needsAttention')}</p>
            </div>
          </div>
          {/* <div className="outer-circle out-of-service">
            <div className="inner-circle">
              <ChargingPoint />
              <p>Out of Service</p>
            </div>
          </div> */}
        </div>
      </div>
    </div>
  );
}
